import React, { startTransition } from 'react';
import { useAppState } from 'src/state-management/state-provider';
import FullPageMessage from 'src/view/components/FullPageMessage';
import { Add } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Project, Site } from 'src/api/Api';

interface RequireSitesWrapperProps {
  children: React.ReactNode;
}

const RequireSitesWrapper: React.FC<RequireSitesWrapperProps> = ({ children }) => {
  const { state } = useAppState();
  const navigate = useNavigate();
  const project = state.selectedProject?.projectDetails;

  const handleAddSite = (project: Project) => {
    startTransition(() => {
      navigate(`/project/${project?.project_id}/add-site`);
    });
  };

  if (project && (!project.sites || project.sites.length === 0)) {
    return (
      <FullPageMessage
        headline="No Sites in Project"
        subHeadline="Please add a site to your project to access this module."
        ctaText="Add Site"
        onCtaClick={() => handleAddSite(project)}
        ctaIcon={<Add />}
      />
    );
  }

  return <>{children}</>;
};

export default RequireSitesWrapper;

type AnalysisType = 'carbon' | 'env' | 'bio';

const analysisConfig = {
  carbon: {
    key: 'carbon_analysis_enabled',
    name: 'Carbon',
  },
  env: {
    key: 'env_analysis_enabled',
    name: 'Climate',
  },
  bio: {
    key: 'bio_analysis_enabled',
    name: 'Biodiversity',
  },
};

export const withAnalysisCheck = (analysisType: AnalysisType) => {
  const Wrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { state } = useAppState();
    const navigate = useNavigate();
    const project = state.selectedProject?.projectDetails;
    const config = analysisConfig[analysisType];

    const handleAddSite = () => {
      startTransition(() => {
        navigate(`/project/${project?.project_id}/add-site`);
      });
    };
    if (project) {
      const hasEnabledSite = project.sites?.some(
        (site) => !!site[config.key as keyof Site]
      );

      if (!hasEnabledSite) {
        return (
          <FullPageMessage
            headline={`No Sites with ${config.name} Analysis Enabled`}
            subHeadline={`Please enable ${config.name} analysis on at least one site to access this module.`}
            ctaText="Add Site"
            onCtaClick={handleAddSite}
            ctaIcon={<Add />}
          />
        );
      }
    }

    return <>{children}</>;
  };

  Wrapper.displayName = `withAnalysisCheck(${analysisType})`;
  return Wrapper;
};